import { Component, ViewEncapsulation } from '@angular/core';
import { BaseBPProfilesDetailComponent } from '../base-bp-profiles-detail.component';
import { BPProfilesService, BPProfilesViewCode, IStatusIconLegend } from '../../bp-profiles.service';
import { Override } from '../../../../shared/app-decorators';
import { BPProfilesTCDataService } from './bp-profiles-tc-data.service';
import { util } from '../../../../shared/util';
import { IProfileAttributeResponse, BPProfilesAttributesService, IProfileAttribute, ProfileAttributeCode, IProfileAttributeViewModel } from '../../profile-attributes/bp-profiles-attributes.service';
import { GaDeferredService, IGADeferred } from '../../../../shared/services/ga-deferred.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'bp-profiles-tc',
  template: `
    <div class="alert alert-warning" *ngIf="editingMode">
      <p><i class="fa fa-exclamation-triangle"></i><span class="margin-left-sm" [innerHTML]="'BPPROFILES.TC.CHANGES_INFO' | translate"></span></p>
    </div>

    <div class="d-block">
      <p [innerHTML]="'BPPROFILES.TC.TC_INFO' | translate"></p>
      <info-link [(linkVariable)]="iconInfoToggle">{{ 'BPPROFILES.STATUS_ICON_INFORMATION' | translate }}</info-link>

      <div class="d-block margin-top-xs" *ngIf="iconInfoToggle">
        <ul class="tc-legend">
          <li *ngFor="let tcLegend of tcLegends">
            <i [ngClass]="tcLegend.iconClass"></i>
            <span class="margin-left-sm">{{ tcLegend.label | translate }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="d-block margin-top-sm">
      <h2 *ngIf="loading">
        <activity-indicator></activity-indicator>
      </h2>
      
      <ng-container *ngIf="!loading">
        <div class="tc-group" *ngFor="let group of model | keyvalue: orderKeyValue">
          <h4 class="tc-title">
            <text [key]="group.key"></text>
          </h4>
          <bp-profiles-attributes [(model)]="group.value" [editingMode]="editingMode" [readOnlyFields]="readOnlyTC" viewCode="${BPProfilesViewCode.TC}"></bp-profiles-attributes>
        </div>
      </ng-container>
    </div>
  `,
  styleUrls: ['./bp-profiles-tc.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BPProfilesTCComponent extends BaseBPProfilesDetailComponent {
  public model:IProfileAttributeViewModel = {};
  public editingMode:boolean;
  public iconInfoToggle:boolean = false;
  public tcLegends:IStatusIconLegend[] = this.bpProfilesTCDataService.tcLegends;
  public readonly readOnlyTC:string[] = [
    ProfileAttributeCode.SUPER_BP_MARGIN,
    ProfileAttributeCode.RESELLER_MARGIN,
    ProfileAttributeCode.STANDARD_PAYMENT_TERMS,
    ProfileAttributeCode.INVOICE_CURRENCY_USD_FOREIGN,
    ProfileAttributeCode.INVOICE_CURRENCY_EUR_FOREIGN
  ];

  private backupTCData:IProfileAttributeResponse;
  private onValidationDeferred:IGADeferred<void>;
  private onValidationSuccessSubscription:Subscription;
  private onValidationErrorSubscription:Subscription;

  constructor(
    private bpProfilesTCDataService:BPProfilesTCDataService,
    private bpProfilesAttributesService:BPProfilesAttributesService,
    private deferredService:GaDeferredService,
    protected bpProfilesService:BPProfilesService
  ) {
    super(bpProfilesService);
  }

  @Override(BaseBPProfilesDetailComponent)
  public ngOnInit():void {
    this.editingMode = this.bpProfilesService.editingMode;
    this.loading = true;
    this.bpProfilesTCDataService.loadTC(this.bpProfilesService.activeProfile?.id)
      .then(this.onTCDataLoaded)
      .finally(() => {
        this.loading = false;
      });

    // register listener
    this.listen();
  }

  @Override(BaseBPProfilesDetailComponent)
  public listen():void {
    super.listen();
    this.onValidationSuccessSubscription = this.bpProfilesAttributesService.onValidationSuccess.subscribe(this.onValidationSuccess);
    this.onValidationErrorSubscription = this.bpProfilesAttributesService.onValidationError.subscribe(this.onValidationError);
  }

  private onTCDataLoaded = (res:IProfileAttributeResponse):void => {
    this.backupTCData = util.copyAndExtend(res);
    this.model = this.bpProfilesAttributesService.processTCModel(res.profileAttributes);
  };

  public orderKeyValue():number {
    // preserve original order of key values
    return 0;
  }

  @Override(BaseBPProfilesDetailComponent)
  public onEditChange(isEdit:boolean):void {
    this.editingMode = isEdit;

    if (!isEdit) {
      this.resetModel();
    }
  }

  @Override(BaseBPProfilesDetailComponent)
  public resetModel():void {
    this.model = this.bpProfilesAttributesService.processTCModel(util.copyAndExtend(this.backupTCData.profileAttributes));
  }

  protected onSave = ():void => {
    this.onValidationDeferred = this.deferredService.defer();
    this.bpProfilesAttributesService.emitValidationStart();

    this.setSaveBusy(true);
    this.onValidationDeferred.promise
      .then(() => {
        const flatModel:IProfileAttribute[] = this.bpProfilesAttributesService.flattenModel(this.model);
        return this.bpProfilesTCDataService.saveTC(this.bpProfilesService.activeProfile.id, this.bpProfilesAttributesService.createProfileAttributesRequestData(flatModel, this.backupTCData.profileAttributes))
          .then(this.onTCDataLoaded);
      })
      .catch(this.emitErrorSaving)
      .finally(() => {
        this.setSaveBusy(false);
      });
  };

  public onValidationSuccess = ():void => {
    this.onValidationDeferred.resolve();
  };

  public onValidationError = ():void => {
    this.onValidationDeferred.reject();
  };

  @Override(BaseBPProfilesDetailComponent)
  public ngOnDestroy() {
    super.ngOnDestroy();
    this.onValidationSuccessSubscription?.unsubscribe();
    this.onValidationErrorSubscription?.unsubscribe();
  }
}